/**
 * A deliberately tiny PDF 1.4 writer — just enough to lay out a single-page,
 * vector-and-text certificate with no third-party dependency.
 *
 * Scope is intentionally narrow: one page, the base-14 Helvetica pair (resolved
 * by the viewer, so no font is embedded), solid fills, strokes, and the path
 * operators our logo marks compile to. Anything beyond that — images, multiple
 * pages, transparency, embedded fonts — is out of scope on purpose; reach for a
 * real PDF library before growing this file into one.
 *
 * PDF's user space is y-up with the origin at the bottom-left of the page, and
 * it has no notion of text alignment — every string is placed at an explicit
 * baseline position. `measureText` in ./fontMetrics is what makes centring and
 * wrapping possible here.
 */

import { measureText, type PdfFont } from './fontMetrics';

export type { PdfFont };

/** US Letter, landscape, in PostScript points (72pt = 1in). */
export const PAGE_WIDTH = 792;
export const PAGE_HEIGHT = 612;

export type Rgb = readonly [number, number, number];

/** `#rrggbb` (or `#rgb`) to PDF's 0–1 component triple. */
export function rgb(hex: string): Rgb {
  const raw = hex.replace('#', '');
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw;
  const value = Number.parseInt(full, 16);
  return [
    ((value >> 16) & 0xff) / 255,
    ((value >> 8) & 0xff) / 255,
    (value & 0xff) / 255,
  ];
}

function fmt(n: number): string {
  // Trim float noise; PDF numbers have no exponent notation, so avoid producing any.
  return (Math.round(n * 1000) / 1000).toString();
}

function colorOps(color: Rgb, stroke = false): string {
  return `${fmt(color[0])} ${fmt(color[1])} ${fmt(color[2])} ${stroke ? 'RG' : 'rg'}`;
}

/**
 * Curly quotes, dashes and other typographic characters that have no slot in
 * WinAnsiEncoding's Latin-1 range, folded to ASCII equivalents. Course titles
 * come from the CMS and routinely contain these.
 */
const TRANSLITERATIONS: Record<string, string> = {
  '‘': "'",
  '’': "'",
  '‚': ',',
  '“': '"',
  '”': '"',
  '–': '-',
  '—': '-',
  '−': '-',
  '…': '...',
  '•': '*',
  ' ': ' ',
  '‹': '<',
  '›': '>',
  '⁄': '/',
};

/**
 * Fold `text` into the Latin-1 range the base-14 fonts can actually render.
 *
 * Characters outside Latin-1 after transliteration (CJK, Cyrillic, Greek, …)
 * have no glyph in a non-embedded Helvetica and are dropped rather than shown
 * as mojibake. Rendering those scripts needs an embedded font — see the module
 * header. Callers that must know whether anything was lost can compare lengths.
 */
export function toLatin1(text: string): string {
  let out = '';
  for (const char of text) {
    const mapped = TRANSLITERATIONS[char];
    if (mapped !== undefined) {
      out += mapped;
      continue;
    }
    const code = char.codePointAt(0) ?? 0;
    if (code >= 0x20 && code <= 0xff) out += char;
    else if (code === 0x09) out += ' ';
  }
  return out;
}

function escapeString(text: string): string {
  return text.replace(/([\\()])/g, '\\$1');
}

export type TextOptions = {
  font?: PdfFont;
  size?: number;
  color?: Rgb;
  /** Extra tracking between glyphs, in points (PDF's `Tc`). */
  charSpacing?: number;
};

/**
 * Accumulates page content-stream operators. Coordinates are PDF user space:
 * origin bottom-left, y increasing upward.
 */
export class PdfPage {
  private readonly ops: string[] = [];

  /** Filled rectangle. */
  rect(x: number, y: number, width: number, height: number, color: Rgb): this {
    this.ops.push(
      `q ${colorOps(color)} ${fmt(x)} ${fmt(y)} ${fmt(width)} ${fmt(height)} re f Q`,
    );
    return this;
  }

  /** Stroked (outline) rectangle. */
  strokeRect(
    x: number,
    y: number,
    width: number,
    height: number,
    color: Rgb,
    lineWidth = 1,
  ): this {
    this.ops.push(
      `q ${colorOps(color, true)} ${fmt(lineWidth)} w ${fmt(x)} ${fmt(y)} ${fmt(width)} ${fmt(height)} re S Q`,
    );
    return this;
  }

  /** Vertical rule from (`x`, `y`) upward. */
  vline(x: number, y: number, height: number, color: Rgb, lineWidth = 0.6): this {
    this.ops.push(
      `q ${colorOps(color, true)} ${fmt(lineWidth)} w ${fmt(x)} ${fmt(y)} m ${fmt(x)} ${fmt(y + height)} l S Q`,
    );
    return this;
  }

  /** Horizontal rule centred on `centerX`. */
  hairline(
    centerX: number,
    y: number,
    width: number,
    color: Rgb,
    lineWidth = 0.75,
  ): this {
    const half = width / 2;
    this.ops.push(
      `q ${colorOps(color, true)} ${fmt(lineWidth)} w ${fmt(centerX - half)} ${fmt(y)} m ${fmt(centerX + half)} ${fmt(y)} l S Q`,
    );
    return this;
  }

  /**
   * Draw pre-built path operators (see ./lionMarks) scaled into a box whose
   * bottom-left corner is (`x`, `y`), preserving the mark's aspect ratio.
   */
  vectorMark(
    mark: { width: number; height: number; path: string },
    x: number,
    y: number,
    targetHeight: number,
    color: Rgb,
  ): this {
    const scale = targetHeight / mark.height;
    this.ops.push(
      `q ${colorOps(color)} ${fmt(scale)} 0 0 ${fmt(scale)} ${fmt(x)} ${fmt(y)} cm ${mark.path} f Q`,
    );
    return this;
  }

  /** Scaled width of a mark drawn at `targetHeight`. */
  static markWidth(
    mark: { width: number; height: number },
    targetHeight: number,
  ): number {
    return (mark.width / mark.height) * targetHeight;
  }

  /** Text with its left edge at `x`, baseline at `y`. */
  text(text: string, x: number, y: number, options: TextOptions = {}): this {
    const {
      font = 'Helvetica',
      size = 11,
      color = [0, 0, 0],
      charSpacing = 0,
    } = options;
    const safe = toLatin1(text);
    if (!safe) return this;
    this.ops.push(
      `BT ${colorOps(color)} /${font === 'Helvetica-Bold' ? 'F2' : 'F1'} ${fmt(size)} Tf ` +
        `${fmt(charSpacing)} Tc 1 0 0 1 ${fmt(x)} ${fmt(y)} Tm (${escapeString(safe)}) Tj ET`,
    );
    return this;
  }

  /** Text centred horizontally on `centerX`, baseline at `y`. */
  centeredText(
    text: string,
    centerX: number,
    y: number,
    options: TextOptions = {},
  ): this {
    const { font = 'Helvetica', size = 11, charSpacing = 0 } = options;
    const width = measureText(toLatin1(text), font, size, charSpacing);
    return this.text(text, centerX - width / 2, y, options);
  }

  /** Operators for this page's content stream. */
  build(): string {
    return this.ops.join('\n');
  }
}

export type PdfMetadata = {
  title?: string;
  author?: string;
  subject?: string;
};

function metadataValue(text: string): string {
  return `(${escapeString(toLatin1(text))})`;
}

/**
 * Serialise a single-page document. Cross-reference offsets are byte offsets,
 * so the document is assembled as a string of code points 0–255 and only then
 * widened to bytes — every character here is one byte by construction.
 */
export function renderPdf(
  page: PdfPage,
  metadata: PdfMetadata = {},
): Uint8Array<ArrayBuffer> {
  const content = page.build();

  const objects = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_WIDTH} ${PAGE_HEIGHT}] ` +
      '/Resources << /Font << /F1 5 0 R /F2 6 0 R >> >> /Contents 4 0 R >>',
    `<< /Length ${content.length} >>\nstream\n${content}\nendstream`,
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>',
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>',
    '<< ' +
      [
        metadata.title ? `/Title ${metadataValue(metadata.title)}` : '',
        metadata.author ? `/Author ${metadataValue(metadata.author)}` : '',
        metadata.subject ? `/Subject ${metadataValue(metadata.subject)}` : '',
        '/Producer (Lion Energy Certification Portal)',
      ]
        .filter(Boolean)
        .join(' ') +
      ' >>',
  ];

  let body = '%PDF-1.4\n';
  const offsets: number[] = [];
  objects.forEach((obj, index) => {
    offsets.push(body.length);
    body += `${index + 1} 0 obj\n${obj}\nendobj\n`;
  });

  const xrefOffset = body.length;
  let xref = `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  for (const offset of offsets) {
    xref += `${offset.toString().padStart(10, '0')} 00000 n \n`;
  }

  const trailer =
    `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R /Info ${objects.length} 0 R >>\n` +
    `startxref\n${xrefOffset}\n%%EOF\n`;

  const document = body + xref + trailer;
  const bytes = new Uint8Array(new ArrayBuffer(document.length));
  for (let i = 0; i < document.length; i++) bytes[i] = document.charCodeAt(i) & 0xff;

  return bytes;
}
